import Sortable from "sortablejs";

import { showToast } from "../utils/toast";
import { closePopover, openReasonPopover, openStagePicker, REJECT } from "./popovers";
import { applyResponse, pageData, pipelineRoot, PipelineReasonData, PipelineStageData, updateEntry } from "./request";

const REJECTED = "rejected";

function firstName(card: HTMLElement): string {
  return (card.dataset.name || "").split(" ")[0] || "candidate";
}

function revert(item: HTMLElement, from: HTMLElement, oldIndex: number | undefined): void {
  item.classList.remove("is-pending");
  from.insertBefore(item, from.children[oldIndex ?? from.children.length] || null);
}

function listFrom<T>(card: HTMLElement, key: "stages" | "reasons"): T[] | undefined {
  const raw = card.closest<HTMLElement>(`[data-${key}]`)?.dataset[key];
  if (raw) {
    try {
      return JSON.parse(raw) as T[];
    } catch {
      // fall through to the page data
    }
  }
  const byBot = key === "stages" ? pageData().stages_by_bot : pageData().reasons_by_bot;
  const botId = card.dataset.botId;
  return (botId && (byBot?.[botId] as unknown as T[] | undefined)) || undefined;
}

function stageForDrop(zone: HTMLElement, card: HTMLElement): string | null {
  const target = zone.dataset.plDropzone!;
  if (target === REJECTED) return REJECTED;
  try {
    const map = JSON.parse(zone.dataset.stageMap || "{}") as Record<string, string>;
    if (Object.keys(map).length === 0) return target;
    return (card.dataset.botId && map[card.dataset.botId]) || null;
  } catch {
    return target;
  }
}

async function move(card: HTMLElement, body: Record<string, unknown>, onFail?: () => void): Promise<void> {
  card.classList.add("is-pending");
  const inDrawer = !!card.closest("[data-pl-drawer]");
  const ok = applyResponse(await updateEntry(card.dataset.plCard!, inDrawer ? { ...body, drawer: "1" } : body));
  if (!ok) {
    card.classList.remove("is-pending");
    onFail?.();
  }
}

export function askReject(card: HTMLElement, anchor: HTMLElement, onCancel?: () => void): void {
  openReasonPopover(anchor, `Reject ${firstName(card)} — reason`, (reasonId) => void move(card, { exit_reason_id: reasonId }, onCancel), {
    onCancel,
    reasons: listFrom<PipelineReasonData>(card, "reasons"),
  });
}

export function askStage(card: HTMLElement, anchor: HTMLElement): void {
  openStagePicker(
    anchor,
    card.dataset.stageId || null,
    (value) => {
      if (value === REJECT) askReject(card, anchor);
      else if (value !== card.dataset.stageId) void move(card, { stage_id: value });
    },
    { stages: listFrom<PipelineStageData>(card, "stages") },
  );
}

export function bindBoard(root: HTMLElement | null = pipelineRoot()): void {
  root?.querySelectorAll<HTMLElement>("[data-pl-dropzone]").forEach((zone) => {
    if (Sortable.get(zone)) return;
    Sortable.create(zone, {
      group: { name: "pl", pull: true, put: true },
      sort: false,
      draggable: '.pl-card[data-draggable="true"]',
      filter: ".pl-ib, .pl-more",
      preventOnFilter: false,
      animation: 120,
      ghostClass: "pl-ghost",
      chosenClass: "is-dragging",
      onMove: (evt) => {
        root.querySelectorAll(".pl-col.is-over").forEach((col) => col.classList.remove("is-over"));
        (evt.to.closest(".pl-col") as HTMLElement | null)?.classList.add("is-over");
        return true;
      },
      onEnd: (evt) => {
        root.querySelectorAll(".pl-col.is-over").forEach((col) => col.classList.remove("is-over"));
        const item = evt.item as HTMLElement;
        const from = evt.from as HTMLElement;
        const to = evt.to as HTMLElement;
        if (from === to) return;
        const target = stageForDrop(to, item);
        const undo = () => revert(item, from, evt.oldIndex);
        if (target === REJECTED) askReject(item, item, undo);
        else if (target) void move(item, { stage_id: target }, undo);
        else {
          undo();
          showToast("That column has no matching stage in this candidate's campaign.", "error", 3200);
        }
      },
    });
  });
}

async function showMore(button: HTMLButtonElement): Promise<void> {
  const root = pipelineRoot();
  const url = new URL(root!.dataset.columnUrl!, window.location.origin);
  Object.entries(pageData(root).filters).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((item) => url.searchParams.append(`${key}[]`, item));
    else url.searchParams.set(key, value);
  });
  url.searchParams.set("stage_id", button.dataset.plMore!);
  url.searchParams.set("offset", button.dataset.offset || "0");
  if (button.dataset.list) url.searchParams.set("view", "list");
  button.disabled = true;
  try {
    const response = await fetch(url.toString(), { headers: { Accept: "text/html" }, credentials: "same-origin" });
    if (!response.ok) throw new Error(String(response.status));
    const html = await response.text();
    const holder = button.dataset.list ? button.closest("tr") : button;
    holder?.insertAdjacentHTML("beforebegin", html);
    holder?.remove();
  } catch {
    button.disabled = false;
    showToast("Couldn't load more candidates. Try again.", "error", 3000);
  }
}

let bound = false;

export function initBoardActions(onOpen: (card: HTMLElement) => void): void {
  if (bound) return;
  bound = true;
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (!target.closest("[data-pl-root]")) return;
    const card = target.closest<HTMLElement>("[data-pl-card]");
    const advance = target.closest<HTMLElement>("[data-pl-advance]");
    const reject = target.closest<HTMLElement>("[data-pl-reject]");
    const more = target.closest<HTMLButtonElement>("[data-pl-more]");
    const pill = target.closest<HTMLElement>("[data-pl-stage-pill]");
    if (pill && card) {
      event.stopPropagation();
      askStage(card, pill);
    } else if (more) {
      event.preventDefault();
      void showMore(more);
    } else if (advance && card) {
      event.stopPropagation();
      closePopover();
      void move(card, { advance: 1 });
    } else if (reject && card) {
      event.stopPropagation();
      askReject(card, reject);
    } else if (card && !target.closest("button, a, input, label, textarea, [data-pl-drawer]")) {
      onOpen(card);
    }
  });
  document.addEventListener("keydown", (event) => {
    const card = (event.target as HTMLElement).closest?.<HTMLElement>(".pl-card[data-pl-card]");
    if (card && event.key === "Enter" && event.target === card && !card.closest("[data-pl-drawer]")) onOpen(card);
  });
}
